import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Avatar,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  People as FamilyIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Language as LanguageIcon,
  EmojiEvents as AchievementIcon,
  Favorite as HeartIcon,
  ContactPhone as ContactIcon,
  HealthAndSafety as HealthIcon
} from '@mui/icons-material';

// Project import
import { gridSpacing } from 'config.js';
import { candidatesData } from '../../Data/Kontak/Candidate';
import { generateCandidatePrintHTML, openPrintPreview } from '../../utils/printUtils';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';

const ViewCandidate = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const response = await fetch(`https://apidashboard.eurekagroup.id/candidates/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        const candidateData = result.data || result;
        // Map _id to id for frontend compatibility
        const mappedCandidate = {
          ...candidateData,
          id: candidateData._id || candidateData.id
        };
        setCandidate(mappedCandidate);
        console.log('Candidate loaded:', result);
      } catch (error) {
        console.error('Error fetching candidate:', error);
        // Fallback to static data
        const candidateData = candidatesData.find(c => c.id === parseInt(id));
        setCandidate(candidateData);
        Swal.fire({
          ...SwalConfig.error,
          title: 'Warning!',
          text: 'Gagal memuat data dari server, menggunakan data lokal'
        });
      }
    };

    fetchCandidate();
  }, [id]);

  if (!candidate) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        gap: 3
      }}>
        <Box sx={{ position: 'relative' }}>
          <CircularProgress 
            size={60} 
            thickness={4}
            sx={{ 
              color: 'primary.main',
              animationDuration: '550ms'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <PersonIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Memuat Data Kandidat
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Mohon tunggu sebentar...
          </Typography>
        </Box>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    const numericAmount = amount.toString().replace(/\./g, '');
    return `Rp ${parseInt(numericAmount).toLocaleString('id-ID')}`;
  };

  const handlePrint = () => {
    if (candidate) {
      // Debug: log the candidate object passed to the print util
      try { console.debug('ViewCandidate.handlePrint - candidate:', candidate); } catch (e) {}
      const printHTML = generateCandidatePrintHTML(candidate);
      
      // Create Blob from HTML string
      const blob = new Blob([printHTML], { type: 'text/html' });
      const blobURL = URL.createObjectURL(blob);
      
      // Open in new window with blob URL
      const printWindow = window.open(blobURL, '_blank');
      
      // Trigger print dialog after content loads
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleConvertToEmployee = (candidateData) => {
    // Navigate to Add Employee page with candidate data
    // Pass the entire candidate data object so AddEmployee can use it
    navigate('/kontak/employee/add', { 
      state: { 
        candidateId: candidateData.id || candidateData._id,
        candidateData: candidateData, // Pass the full candidate data
        fromCandidate: true,
        mode: 'simple' // Set to simple mode by default
      } 
    });
  };

  return (
    <Box sx={{ p: 1.5 }} className="animate-fade-in">
      {/* Compact Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton 
            onClick={() => navigate('/kontak/candidate')}
            size="small"
            sx={{ 
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.action.hover : 'grey.100',
              '&:hover': { 
                bgcolor: theme.palette.mode === 'dark' ? theme.palette.action.selected : 'grey.200' 
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Detail Kandidat
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 2
            }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/kontak/candidate/edit/${candidate.id}`)}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 2
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<WorkIcon />}
            onClick={() => handleConvertToEmployee(candidate)}
            sx={{ 
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 2
            }}
          >
            Jadikan Karyawan
          </Button>
        </Box>
      </Box>

      {/* Profile Summary Card */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
                <Avatar
                  src={candidate.personalData.photo}
                  alt={candidate.personalData.full_name}
                sx={{ width: 80, height: 80, border: '3px solid', borderColor: 'primary.light' }}
                />
            </Grid>
            <Grid item xs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {candidate.personalData.full_name}
                </Typography>
                <Chip 
                  label={candidate.applicationStatus} 
                  color={candidate.applicationStatus === 'Active' ? 'success' : 'error'}
                  size="small"
                  sx={{ fontWeight: 600, height: 22 }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                {candidate.personalData.gender} • {candidate.personalData.age} tahun • {candidate.personalData.ethnicity}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {candidate.personalData.phone}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {candidate.personalData.address}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {formatDate(candidate.personalData.birth_date)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
            </CardContent>
          </Card>

      {/* Tabs Navigation */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
                  sx={{ 
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem'
            }
          }}
        >
          <Tab label="Data Pribadi" icon={<PersonIcon />} iconPosition="start" />
          <Tab label="Riwayat" icon={<InfoIcon />} iconPosition="start" />
          <Tab label="Pendidikan" icon={<SchoolIcon />} iconPosition="start" />
          <Tab label="Keluarga" icon={<FamilyIcon />} iconPosition="start" />
          <Tab label="Lain-lain" icon={<ContactIcon />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Tab Content */}
      <Box sx={{ width: '100%' }}>
        {activeTab === 0 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                {[
                  { label: 'Tempat, Tanggal Lahir', value: `${candidate.personalData.birth_place}, ${formatDate(candidate.personalData.birth_date)}`, icon: <CalendarIcon /> },
                  { label: 'Agama', value: candidate.personalData.religion, icon: <HeartIcon /> },
                  { label: 'Telepon', value: candidate.personalData.phone, icon: <PhoneIcon />, important: true },
                  { label: 'Alamat', value: candidate.personalData.address, icon: <LocationIcon /> },
                  { label: 'ID Number', value: candidate.personalData.id_number, icon: <BusinessIcon /> },
                  { label: 'Prestasi Terbaik', value: `${candidate.personalData.best_achievement} - ${candidate.personalData.best_achievement_desc}`, icon: <AchievementIcon /> },
                  { label: 'Prestasi Terburuk', value: `${candidate.personalData.worst_achievement || '-'} - ${candidate.personalData.worst_achievement_desc || '-'}`, icon: <AchievementIcon /> },
                  { label: 'Bahasa Aktif', value: candidate.personalData.language_active || '-', icon: <LanguageIcon /> },
                  { label: 'Bahasa Pasif', value: candidate.personalData.language_passive || '-', icon: <LanguageIcon /> },
                  { label: 'Status Tempat Tinggal', value: candidate.personalData.living_status || '-', icon: <LocationIcon /> },
                  { label: 'Jenis Kendaraan', value: candidate.personalData.vehicle_type || '-', icon: <BusinessIcon /> },
                  { label: 'Kepemilikan Kendaraan', value: candidate.personalData.vehicle_ownership || '-', icon: <BusinessIcon /> },
                ].map(({ label, value, icon, important }, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: 'rgba(158, 158, 158, 0.05)',
                      border: '1px solid rgba(158, 158, 158, 0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {React.cloneElement(icon, { sx: { color: 'grey.600', fontSize: 18, mr: 1 } })}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700' }}>
                          {label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: important ? 'primary.main' : 'text.primary', fontWeight: important ? 600 : 400 }}>
                        {value || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {activeTab === 1 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Alasan Keluar Kerja Terakhir
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.history?.last_job_reason_leave || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Gaji Terakhir
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                        {candidate?.history?.last_job_salary ? formatCurrency(candidate.history.last_job_salary) : '-'}
                      </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Pekerjaan yang Disukai
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.history?.job_most_liked || '-'}
                    </Typography>
                  </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Hobi
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.history?.hobby || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Waktu Luang
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.history?.free_time || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Topik Bacaan
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.history?.reading_topics || '-'}
                    </Typography>
                  </Box>
                    </Grid>
            </Grid>

              {/* Work History Table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Riwayat Pekerjaan
              </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Masuk</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Keluar</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Jabatan</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Perusahaan</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Alamat & Telp</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Keterangan</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {candidate?.workHistory && candidate.workHistory.length > 0 ? (
                          candidate.workHistory.map((work, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{work.masuk ? formatDate(work.masuk) : '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{work.keluar ? formatDate(work.keluar) : '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{work.jabatan || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{work.perusahaan || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{work.alamat_telp || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{work.keterangan || '-'}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                              Tidak ada riwayat pekerjaan
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

              {/* Social Activities */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, mt: 2 }}>
                Aktivitas Sosial
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tahun</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Jenis Aktivitas</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Jabatan</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Organisasi</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {candidate?.socialActivities && candidate.socialActivities.length > 0 ? (
                          candidate.socialActivities.map((activity, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{activity.tahun || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{activity.jenis_aktivitas || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{activity.jabatan || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{activity.organisasi || '-'}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                              Tidak ada aktivitas sosial
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
        )}

        {activeTab === 2 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              {/* Lamaran & Keterampilan */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Posisi yang Dilamar
                  </Typography>
                    <Typography variant="body2">
                      {candidate?.applicationSkills?.desired_position || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Gaji yang Diharapkan
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                      {candidate?.applicationSkills?.expected_salary ? formatCurrency(candidate.applicationSkills.expected_salary) : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Alasan Melamar
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.applicationSkills?.apply_reason || '-'}
                    </Typography>
                  </Box>
            </Grid>
                    <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Keahlian
                      </Typography>
                    <Typography variant="body2">
                        {candidate?.applicationSkills?.skills || '-'}
                      </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Program Komputer
                      </Typography>
                    <Typography variant="body2">
                        {candidate?.applicationSkills?.computer_programs || '-'}
                      </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Kemampuan Mengemudi
                      </Typography>
                      <Typography variant="body2">
                        {candidate?.applicationSkills?.driving_ability || '-'}
                      </Typography>
                  </Box>
                    </Grid>
            </Grid>

              {/* Pendidikan Formal */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Pendidikan Formal
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Jenjang</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Nama Sekolah</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tempat</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tahun Lulus</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Nilai</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Jurusan</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {candidate?.education && candidate.education.length > 0 ? (
                          candidate.education.map((edu, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{edu.jenjang || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{edu.nama_sekolah || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{edu.tempat || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{edu.tahun_lulus || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem', color: 'success.main', fontWeight: 600 }}>{edu.rata_rata_nilai || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{edu.jurusan || '-'}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                              Tidak ada data pendidikan
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

              {/* Kursus */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Kursus/Pelatihan
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Instruktur/Lembaga</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tempat</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Lama</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Pembiayaan</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Keterangan</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {candidate?.courses && candidate.courses.length > 0 ? (
                          candidate.courses.map((course, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{course.instruktur_lembaga || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{course.tempat || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{course.lama_pendidikan || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{course.yang_membiayai || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{course.keterangan || '-'}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                              Tidak ada data kursus/pelatihan
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
        )}

        {activeTab === 3 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Status Pernikahan
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.family?.marital_status || '-'}
                    </Typography>
                  </Box>
                </Grid>
            </Grid>

              {/* Family Data Table */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Data Keluarga
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Jenis</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Nama</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>L/P</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Usia</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Pekerjaan</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidate?.family?.members && candidate.family.members.length > 0 ? (
                      candidate.family.members.map((member, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{member.jenis || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{member.nama || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{member.gender || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{member.usia || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{member.pekerjaan || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          Tidak ada data keluarga
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeTab === 4 && (
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              {/* Umum & Kesehatan */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Kekuatan
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      {candidate?.generalHealth?.strength || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Kelemahan
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'warning.main' }}>
                      {candidate?.generalHealth?.weakness || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Penyakit Umum
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.generalHealth?.common_illness || '-'}
                    </Typography>
                  </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Reaksi terhadap Kritik
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.generalHealth?.reaction_to_criticism || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)',
                    mb: 1.5
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Checkup Terakhir
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.generalHealth?.last_checkup ? formatDate(candidate.generalHealth.last_checkup) : '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'rgba(158, 158, 158, 0.05)',
                    border: '1px solid rgba(158, 158, 158, 0.1)'
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.700', display: 'block', mb: 0.5 }}>
                      Sumber Saran Pribadi
                    </Typography>
                    <Typography variant="body2">
                      {candidate?.generalHealth?.personal_advice_source || '-'}
                    </Typography>
                  </Box>
                    </Grid>
            </Grid>

              {/* Relations Tables */}
              <Grid container spacing={2}>
                    {/* Relasi saat berhalangan */}
                    <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                        Relasi saat berhalangan
                      </Typography>
                  <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Nama</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Usia</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Alamat</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Telepon</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Hubungan</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {candidate?.others?.emergency_relations && candidate.others.emergency_relations.length > 0 ? (
                              candidate.others.emergency_relations.map((relation, index) => (
                                <TableRow key={index}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.nama || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.usia || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.alamat || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.no_telepon || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.hubungan || '-'}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
                                  Tidak ada data
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {/* Eks atasan/keluarga */}
                    <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'secondary.main' }}>
                        Eks atasan/keluarga
                      </Typography>
                  <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Nama</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Usia</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Alamat</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Telepon</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Hubungan</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {candidate?.others?.former_relations && candidate.others.former_relations.length > 0 ? (
                              candidate.others.former_relations.map((relation, index) => (
                                <TableRow key={index}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.nama || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.usia || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.alamat || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.no_telepon || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{relation.hubungan || '-'}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
                                  Tidak ada data
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {/* Penjamin di PT Eureka */}
                    <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'success.main' }}>
                        Penjamin di PT Eureka
                      </Typography>
                  <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Nama</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Usia</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Alamat</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Telepon</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Hubungan</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {candidate?.others?.guarantors && candidate.others.guarantors.length > 0 ? (
                              candidate.others.guarantors.map((guarantor, index) => (
                                <TableRow key={index}>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{guarantor.nama || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{guarantor.usia || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{guarantor.alamat || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{guarantor.no_telepon || '-'}</TableCell>
                                  <TableCell sx={{ fontSize: '0.7rem' }}>{guarantor.hubungan || '-'}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
                                  Tidak ada data
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
        )}
      </Box>
    </Box>
  );
};

export default ViewCandidate;